import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Calendar } from '../components/ui/calendar';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  User, 
  Phone, 
  Mail,
  CreditCard,
  CheckCircle,
  Loader2,
  ArrowLeft,
  GraduationCap,
  Star,
  Users,
  Music
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RAZORPAY_KEY = process.env.REACT_APP_RAZORPAY_KEY;

const sessionTypes = {
  mentorship: { label: 'Mentorship Session', icon: GraduationCap, description: 'One-on-one guidance and career advice' },
  song_review: { label: 'Song Review', icon: Star, description: 'Get professional feedback on your music' },
  prayer_session: { label: 'Prayer Session', icon: Users, description: 'Spiritual guidance and prayer time' },
  virtual_concert: { label: 'Virtual Concert', icon: Music, description: 'Private online performance' }
};

const BookingPage = () => {
  const { username } = useParams();
  const navigate = useNavigate();
  const { user, token, isAuthenticated } = useAuth();
  
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  const [mentor, setMentor] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [filteredSlots, setFilteredSlots] = useState([]);
  
  const [bookingData, setBookingData] = useState({
    full_name: '',
    email: '',
    phone: '',
    description: '',
    session_type: ''
  });
  
  const [bookingResult, setBookingResult] = useState(null);

  useEffect(() => {
    fetchMentorData();
  }, [username]);

  useEffect(() => {
    if (user && isAuthenticated) {
      setBookingData(prev => ({
        ...prev,
        full_name: user.full_name || '',
        email: user.email || ''
      }));
    }
  }, [user, isAuthenticated]);

  useEffect(() => {
    if (selectedDate && availableSlots.length > 0) {
      const dateStr = format(selectedDate, 'yyyy-MM-dd');
      const filtered = availableSlots.filter(slot => slot.date === dateStr);
      setFilteredSlots(filtered);
      setSelectedSlot(null);
    }
  }, [selectedDate, availableSlots]);

  const fetchMentorData = async () => {
    try {
      const [profileRes, slotsRes] = await Promise.all([
        axios.get(`${API}/users/${username}`),
        axios.get(`${API}/bookings/slots`, { params: { artist_id: null } })
      ]);
      
      if (profileRes.data?.user) {
        setMentor(profileRes.data.user);
        // Filter slots for this mentor
        const mentorSlots = slotsRes.data.filter(s => s.artist_id === profileRes.data.user.id);
        setAvailableSlots(mentorSlots);
      }
    } catch (error) {
      console.error('Failed to fetch mentor data:', error);
      toast.error('Failed to load mentor information');
    } finally {
      setLoading(false);
    }
  };

  const getAvailableDates = () => {
    const dates = [...new Set(availableSlots.map(s => s.date))];
    return dates.map(d => new Date(d));
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
  };

  const handleSlotSelect = (slot) => {
    setSelectedSlot(slot);
    setBookingData(prev => ({ ...prev, session_type: slot.session_type }));
  };

  const handleInputChange = (e) => {
    setBookingData({ ...bookingData, [e.target.name]: e.target.value });
  };

  const validateStep1 = () => {
    if (!selectedDate || !selectedSlot) {
      toast.error('Please select a date and time slot');
      return false;
    }
    return true;
  };

  const validateStep2 = () => {
    if (!bookingData.full_name || !bookingData.email || !bookingData.phone) {
      toast.error('Please fill in all required fields');
      return false;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(bookingData.email)) {
      toast.error('Please enter a valid email address');
      return false;
    }
    if (!/^\+?[\d\s-]{10,}$/.test(bookingData.phone)) {
      toast.error('Please enter a valid phone number');
      return false;
    }
    return true;
  };

  const handleProceedToDetails = () => {
    if (validateStep1()) {
      setStep(2);
    }
  };

  const handleProceedToPayment = async () => {
    if (!validateStep2()) return;
    if (!isAuthenticated) {
      toast.error('Please login to continue');
      navigate('/login');
      return;
    }

    setProcessing(true);
    try {
      // Create booking
      const bookingRes = await axios.post(`${API}/bookings`, {
        slot_id: selectedSlot.id,
        notes: bookingData.description,
        booker_name: bookingData.full_name,
        booker_email: bookingData.email,
        booker_phone: bookingData.phone
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const bookingId = bookingRes.data.id;

      // Create Razorpay order
      const orderRes = await axios.post(`${API}/payments/create-order`, {
        amount: Math.round(selectedSlot.price * 100), // Convert to paise
        currency: 'INR',
        item_type: 'booking',
        item_id: bookingId,
        notes: {
          booking_id: bookingId,
          session_type: selectedSlot.session_type,
          mentor: mentor.full_name
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Initialize Razorpay
      const options = {
        key: RAZORPAY_KEY,
        amount: orderRes.data.amount,
        currency: orderRes.data.currency,
        name: 'M4manna',
        description: `${sessionTypes[selectedSlot.session_type]?.label} with ${mentor.full_name}`,
        order_id: orderRes.data.order_id,
        handler: async (response) => {
          try {
            // Verify payment
            await axios.post(`${API}/payments/verify`, {
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature
            }, {
              headers: { Authorization: `Bearer ${token}` }
            });

            setBookingResult({
              success: true,
              bookingId,
              date: selectedSlot.date,
              time: selectedSlot.time,
              mentor: mentor.full_name,
              sessionType: selectedSlot.session_type
            });
            setStep(3);
            toast.success('Booking confirmed!');
          } catch (error) {
            toast.error('Payment verification failed');
          }
        },
        prefill: {
          name: bookingData.full_name,
          email: bookingData.email,
          contact: bookingData.phone
        },
        theme: {
          color: '#FFE500'
        }
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch (error) {
      console.error('Booking error:', error);
      toast.error(error.response?.data?.detail || 'Failed to process booking');
    } finally {
      setProcessing(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-m4-yellow" />
        </div>
      </Layout>
    );
  }

  if (!mentor) {
    return (
      <Layout>
        <div className="min-h-[60vh] flex items-center justify-center">
          <div className="text-center">
            <User className="h-16 w-16 mx-auto mb-4 text-neutral-700" />
            <h2 className="font-heading text-2xl font-bold text-white">Mentor Not Found</h2>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="py-8 px-4 md:px-8">
        <div className="container mx-auto max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate(-1)}
              className="flex items-center gap-2 text-neutral-400 hover:text-white mb-4 transition-colors"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <p className="text-sm text-m4-yellow uppercase tracking-widest mb-2">Book Session</p>
            <h1 className="font-heading text-4xl font-bold text-white">
              {mentor.full_name}
            </h1>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center gap-4 mb-8">
            {[1, 2, 3].map((s) => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-2 ${step >= s ? 'text-m4-yellow' : 'text-neutral-600'}`}>
                  <div className={`h-8 w-8 rounded-full flex items-center justify-center border-2 ${
                    step >= s ? 'border-m4-yellow bg-m4-yellow/10' : 'border-neutral-700'
                  }`}>
                    {step > s ? <CheckCircle className="h-5 w-5" /> : s}
                  </div>
                  <span className="hidden sm:inline text-sm font-medium">
                    {s === 1 ? 'Select Time' : s === 2 ? 'Your Details' : 'Confirmed'}
                  </span>
                </div>
                {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-m4-yellow' : 'bg-neutral-800'}`} />}
              </React.Fragment>
            ))}
          </div>

          {/* Step 1: Select Date & Time */}
          {step === 1 && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Calendar */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                    <CalendarIcon className="h-5 w-5 text-m4-yellow" />
                    Select Date
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={handleDateSelect}
                    disabled={(date) => {
                      const availableDates = getAvailableDates();
                      return !availableDates.some(d => 
                        d.toDateString() === date.toDateString()
                      );
                    }}
                    className="rounded-md border border-neutral-800"
                  />
                </CardContent>
              </Card>

              {/* Time Slots */}
              <Card className="bg-neutral-900 border-neutral-800">
                <CardHeader>
                  <CardTitle className="font-heading text-lg text-white flex items-center gap-2">
                    <Clock className="h-5 w-5 text-m4-yellow" />
                    Available Slots
                    {selectedDate && (
                      <span className="text-sm font-normal text-neutral-400 ml-2">
                        {format(selectedDate, 'MMM d, yyyy')}
                      </span>
                    )}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {!selectedDate ? (
                    <p className="text-neutral-500 text-center py-8">Select a date to see available slots</p>
                  ) : filteredSlots.length > 0 ? (
                    <div className="space-y-3">
                      {filteredSlots.map((slot) => {
                        const SessionIcon = sessionTypes[slot.session_type]?.icon || Clock;
                        return (
                          <button
                            key={slot.id}
                            onClick={() => handleSlotSelect(slot)}
                            className={`w-full p-4 border text-left transition-all ${
                              selectedSlot?.id === slot.id
                                ? 'border-m4-yellow bg-m4-yellow/10'
                                : 'border-neutral-700 hover:border-neutral-600 bg-neutral-800'
                            }`}
                            data-testid={`slot-${slot.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-3">
                                <SessionIcon className="h-5 w-5 text-m4-yellow" />
                                <div>
                                  <p className="font-medium text-white">{slot.time}</p>
                                  <p className="text-sm text-neutral-400">
                                    {sessionTypes[slot.session_type]?.label} • {slot.duration} min
                                  </p>
                                </div>
                              </div>
                              <span className="text-m4-yellow font-bold">₹{slot.price}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-neutral-500 text-center py-8">No slots available for this date</p>
                  )}
                </CardContent>
              </Card>

              {/* Continue Button */}
              <div className="lg:col-span-2">
                <Button
                  onClick={handleProceedToDetails}
                  disabled={!selectedSlot}
                  className="w-full bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold uppercase tracking-wider py-6"
                  data-testid="continue-to-details-btn"
                >
                  Continue to Details
                </Button>
              </div>
            </div>
          )}

          {/* Step 2: Booking Details */}
          {step === 2 && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Form */}
              <div className="lg:col-span-2">
                <Card className="bg-neutral-900 border-neutral-800">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-white">Your Information</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-neutral-300">Full Name *</Label>
                        <div className="relative">
                          <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                          <Input
                            name="full_name"
                            value={bookingData.full_name}
                            onChange={handleInputChange}
                            placeholder="Your full name"
                            className="pl-10 bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                            data-testid="booking-name"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label className="text-neutral-300">Phone Number *</Label>
                        <div className="relative">
                          <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                          <Input
                            name="phone"
                            value={bookingData.phone}
                            onChange={handleInputChange}
                            placeholder="+91 98765 43210"
                            className="pl-10 bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                            data-testid="booking-phone"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-neutral-300">Email Address *</Label>
                      <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-500" />
                        <Input
                          name="email"
                          type="email"
                          value={bookingData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          className="pl-10 bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow"
                          data-testid="booking-email"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label className="text-neutral-300">What would you like to discuss?</Label>
                      <Textarea
                        name="description"
                        value={bookingData.description}
                        onChange={handleInputChange}
                        placeholder="Share your goals, questions, or topics you'd like to cover in the session..."
                        rows={4}
                        className="bg-neutral-800 border-neutral-700 rounded-xl text-white focus:border-m4-yellow placeholder:text-neutral-500"
                        data-testid="booking-description"
                      />
                    </div>

                    <Button
                      onClick={handleProceedToPayment}
                      disabled={processing}
                      className="w-full bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold uppercase tracking-wider py-6"
                      data-testid="proceed-to-payment-btn"
                    >
                      {processing ? (
                        <><Loader2 className="h-5 w-5 animate-spin mr-2" /> Processing...</>
                      ) : (
                        <><CreditCard className="h-5 w-5 mr-2" /> Pay ₹{selectedSlot?.price}</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>

              {/* Summary */}
              <div>
                <Card className="bg-neutral-900 border-neutral-800 sticky top-24">
                  <CardHeader>
                    <CardTitle className="font-heading text-lg text-white">Booking Summary</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="flex items-center gap-4">
                      <div className="h-12 w-12 rounded-full bg-m4-yellow flex items-center justify-center">
                        <span className="text-xl font-bold text-black">{mentor.full_name?.charAt(0)}</span>
                      </div>
                      <div>
                        <p className="font-medium text-white">{mentor.full_name}</p>
                        <p className="text-sm text-neutral-400">@{mentor.username}</p>
                      </div>
                    </div>

                    <div className="border-t border-neutral-800 pt-4 space-y-3">
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Session</span>
                        <span className="text-white">{sessionTypes[selectedSlot?.session_type]?.label}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Date</span>
                        <span className="text-white">{selectedDate && format(selectedDate, 'MMM d, yyyy')}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Time</span>
                        <span className="text-white">{selectedSlot?.time}</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-neutral-400">Duration</span>
                        <span className="text-white">{selectedSlot?.duration} minutes</span>
                      </div>
                    </div>

                    <div className="border-t border-neutral-800 pt-4">
                      <div className="flex justify-between">
                        <span className="font-medium text-white">Total</span>
                        <span className="font-bold text-m4-yellow text-xl">₹{selectedSlot?.price}</span>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}

          {/* Step 3: Confirmation */}
          {step === 3 && bookingResult && (
            <Card className="bg-neutral-900 border-neutral-800 text-center p-8">
              <div className="h-20 w-20 rounded-full bg-m4-yellow/10 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="h-10 w-10 text-m4-yellow" />
              </div>
              <h2 className="font-heading text-3xl font-bold text-white mb-2">
                BOOKING CONFIRMED!
              </h2>
              <p className="text-neutral-400 mb-8">
                Your session has been booked. A confirmation email will be sent to {bookingData.email}
              </p>

              <div className="bg-neutral-800 p-6 max-w-md mx-auto mb-8 text-left">
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Session with</span>
                    <span className="text-white font-medium">{bookingResult.mentor}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Type</span>
                    <span className="text-white">{sessionTypes[bookingResult.sessionType]?.label}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Date</span>
                    <span className="text-white">{bookingResult.date}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Time</span>
                    <span className="text-white">{bookingResult.time}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-neutral-400">Booking ID</span>
                    <span className="text-m4-yellow font-mono text-sm">{bookingResult.bookingId?.slice(0, 8)}...</span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  onClick={() => navigate('/dashboard')}
                  className="bg-m4-yellow text-black hover:bg-m4-yellow/90 font-bold"
                >
                  Go to Dashboard
                </Button>
                <Button
                  onClick={() => navigate('/mentors')}
                  variant="outline"
                  className="border-neutral-700 text-white hover:bg-neutral-800"
                >
                  Book Another Session
                </Button>
              </div>
            </Card>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default BookingPage;
